import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { CircularProgress, Paper, Snackbar, Stack, Tooltip, Typography } from '@mui/material';
import {
  createContext,
  ReactElement,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useDataProvider, useNotify } from 'react-admin';
import ProgressStrip from '../../../Components/ProgressStrip';
import type { GPDataProvider } from '../../../DataProvider';
import { getJsonApiErrorDetail } from '../../../utils';
import { ClickExportProgress, ClickExportRequest, exportAllClicks } from './utils';

interface ClickExportContextValue {
  isRunning: boolean;
  startExport: (request: ClickExportRequest) => Promise<void>;
}

const ClickExportContext = createContext<ClickExportContextValue | null>(null);

export function useClickExport(): ClickExportContextValue {
  const context = useContext(ClickExportContext);

  if (context == null) {
    throw new Error('useClickExport must be used within ExportAllProvider');
  }

  return context;
}

export default function ExportAllProvider({ children }: { children: ReactNode }): ReactElement {
  const dataProvider = useDataProvider<GPDataProvider>();
  const notify = useNotify();
  const [exportState, setExportState] = useState<ClickExportProgress | null>(null);

  const startExport = useCallback(
    async (request: ClickExportRequest): Promise<void> => {
      if (exportState) {
        notify('Clicks export already in progress.', { type: 'warning' });
        return;
      }

      setExportState({ fetched: 0, phase: 'fetching', total: 0 });

      try {
        const total = await exportAllClicks({
          dataProvider,
          ...request,
          onProgress: setExportState,
        });
        notify(`Exported ${total.toLocaleString()} clicks.`, { type: 'success' });
      } catch (error) {
        notify(getJsonApiErrorDetail(error, 'Clicks export failed.'), { type: 'error' });
      } finally {
        setExportState(null);
      }
    },
    [dataProvider, exportState, notify],
  );

  const value = useMemo<ClickExportContextValue>(
    () => ({
      isRunning: exportState != null,
      startExport,
    }),
    [exportState, startExport],
  );

  const progress =
    exportState && exportState.total > 0 ? (exportState.fetched / exportState.total) * 100 : 0;

  return (
    <ClickExportContext.Provider value={value}>
      {children}
      <Snackbar
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        open={exportState != null}
      >
        <Paper elevation={6} sx={{ minWidth: 360, p: 2 }}>
          <Stack spacing={1}>
            <Stack alignItems="center" direction="row" spacing={1}>
              <CircularProgress size={16} />
              <Typography variant="subtitle2">Exporting clicks</Typography>
              <Tooltip title="Please do not reload the page until the export has finished. You can continue using the CMS while it runs.">
                <InfoOutlinedIcon color="action" fontSize="small" />
              </Tooltip>
            </Stack>
            <Typography color="text.secondary" variant="body2">
              {exportState
                ? exportState.phase === 'fetching'
                  ? `${exportState.fetched.toLocaleString()} of ${exportState.total.toLocaleString()} rows fetched`
                  : 'Resolving related names'
                : ''}
            </Typography>
            <ProgressStrip complete={false} progress={progress} />
          </Stack>
        </Paper>
      </Snackbar>
    </ClickExportContext.Provider>
  );
}
